import { type WorkflowStepField, WorkflowStepFieldKind } from 'src/gql/graphql';
import { WorkflowTriggerType } from 'src/graphql/entities/workflow/workflow.trigger';
import {
  type WorkflowDefinition,
  WorkflowNodeType,
  type WorkflowStepCatalog,
  type WorkflowStepData,
  type WorkflowStepProblem,
} from './workflow-definition';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// The backend's rule: undefined, null and empty text are "not filled in" — except for a SCALAR,
// where empty text is a value an If can compare against.
const isAbsent = (value: unknown, kind: WorkflowStepFieldKind) =>
  value === undefined ||
  value === null ||
  (value === '' && kind !== WorkflowStepFieldKind.Scalar);

const isRequired = (field: WorkflowStepField, siblings: WorkflowStepData) =>
  field.required ||
  (!!field.requiredUnless &&
    !field.requiredUnless.oneOf.includes(siblings[field.requiredUnless.key] as string));

const requiredMessage = (field: WorkflowStepField) =>
  field.requiredUnless
    ? `Required unless "${field.requiredUnless.key}" is ${field.requiredUnless.oneOf.join(' or ')}.`
    : 'Required.';

/** The required-field problems for one step's data, with `field` set to each failing path. */
export function collectFieldProblems({
  nodeId,
  fields,
  data,
  parentPath = '',
}: {
  nodeId: string;
  fields: WorkflowStepField[];
  data: WorkflowStepData | undefined;
  /** The parent field's path plus a dot, for the fields of a group or a condition. */
  parentPath?: string;
}): WorkflowStepProblem[] {
  const values = data ?? {};

  return fields.flatMap((field) => {
    const path = `${parentPath}${field.key}`;
    const value = values[field.key];

    if (isAbsent(value, field.kind)) {
      // filled from an earlier step, so there's nothing to type; the field says what it needs instead
      if (field.reference) return [];
      return isRequired(field, values)
        ? [{ nodeId, field: path, message: requiredMessage(field) }]
        : [];
    }

    // A group, or a switch that holds a condition rather than on / off, has fields of its own.
    // Embedded STEP_LIST items are left to the save, which names them as `content[0].message`.
    const nests =
      field.kind === WorkflowStepFieldKind.Group ||
      field.kind === WorkflowStepFieldKind.BooleanOrCondition;

    return nests && isPlainObject(value)
      ? collectFieldProblems({
          nodeId,
          fields: field.fields ?? [],
          data: value,
          parentPath: `${path}.`,
        })
      : [];
  });
}

/**
 * What the canvas can tell from the catalog alone, before a save: required fields that are missing
 * (honouring `requiredUnless`), steps that can't run on the workflow's trigger, and step types the
 * catalog doesn't describe. An `if` output with nothing after it is fine: the run ends there. "Missing" and the messages match
 * the backend's — undefined, null or empty text — so the canvas never flags what the save accepts.
 *
 * Deliberately not a copy of the backend's validation — the save runs that, and its errors use the
 * same shape, so they land in the same places.
 */
export function collectStepProblems(
  definition: WorkflowDefinition,
  catalog: WorkflowStepCatalog,
): WorkflowStepProblem[] {
  const trigger = definition.nodes.find(({ type }) => type === WorkflowNodeType.Trigger);
  const rawTriggerType = trigger?.data?.triggerType;
  const triggerType =
    typeof rawTriggerType === 'string' ? rawTriggerType : WorkflowTriggerType.InboundPhoneCall;

  return definition.nodes.flatMap((node): WorkflowStepProblem[] => {
    // the run starts at the step after the trigger; the backend refuses a trigger with none
    if (
      node.type === WorkflowNodeType.Trigger &&
      !definition.edges.some(({ source }) => source === node.id)
    ) {
      return [{ nodeId: node.id, message: 'Add a step after the trigger.' }];
    }

    const stepType = catalog.byType.get(node.type);
    if (!stepType) {
      return [
        {
          nodeId: node.id,
          message: `"${node.type}" isn't a supported step type. Delete it to save.`,
        },
      ];
    }

    const runsOnTrigger: WorkflowStepProblem[] = stepType.triggerTypes.includes(triggerType)
      ? []
      : [
          {
            nodeId: node.id,
            message: `"${stepType.label}" can't run on a ${triggerType} workflow.`,
          },
        ];

    return [
      ...runsOnTrigger,
      ...collectFieldProblems({ nodeId: node.id, fields: stepType.fields, data: node.data }),
    ];
  });
}

/** Problems by step id. Problems with no `nodeId` (whole-graph ones) are under `null`. */
export function groupProblemsByNode(
  problems: WorkflowStepProblem[],
): ReadonlyMap<string | null, WorkflowStepProblem[]> {
  return problems.reduce((grouped, problem) => {
    const key = problem.nodeId ?? null;
    grouped.set(key, [...(grouped.get(key) ?? []), problem]);
    return grouped;
  }, new Map<string | null, WorkflowStepProblem[]>());
}
